import React from "react";
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import styled from "@emotion/styled";
import { useTheme } from "@mui/styles";

const TeamStats = ({ teamLeadGenerationStats }) => {
  const theme = useTheme()

  const tooltipSx = {
    color: theme.palette.common.black,
    backgroundColor: theme.palette.common.white,
    border: `0.1rem solid ${theme.palette.common.grayBorder}`,
    boxShadow: '0 0.7rem 1.7rem rgba(15, 23, 42, 0.14)',
    borderRadius: '0.65rem',
    maxWidth: '17rem',
    padding: '0.75rem 0.85rem',
    fontSize: '0.78rem',
    fontWeight: 650,
    lineHeight: 1.4,
  }

  const tier1Percent = teamLeadGenerationStats.totalTier1Leads === 0 ?
    `0`
  :
    (Math.round((teamLeadGenerationStats.totalTier1Leads/teamLeadGenerationStats.totalLeadsGenerated)*100))

  const tier2Percent = teamLeadGenerationStats.totalTier2Leads === 0 ?
    `0`
  :
    (Math.round((teamLeadGenerationStats.totalTier2Leads/teamLeadGenerationStats.totalLeadsGenerated)*100))

  const stats = [
    {
      label: "Records Swept",
      value: teamLeadGenerationStats.totalSuccessfulQueries,
      detail: "Successful scans",
    },
    {
      label: "Total Hits",
      value: teamLeadGenerationStats.totalHits,
      detail: "Detected discrepancies",
    },
    {
      label: "Leads Generated",
      value: teamLeadGenerationStats.totalLeadsGenerated,
      detail: "Created investigations",
    },
    {
      label: "Hit Frequency",
      value: teamLeadGenerationStats.totalHitsAvgPercent.toLocaleString("en-US", {maximumFractionDigits: 0, minimumFractionDigits: 0}),
      suffix: "%",
      detail: "Sweep hit rate",
      tooltip: "How often a sweep of public records detects one or more discrepancies on any property.",
    },
    {
      label: "Total Tier 1",
      value: teamLeadGenerationStats.totalTier1Leads,
      detail: "Generated leads",
      secondaryValue: tier1Percent,
      tooltip: "The value shown in parentheses is the percentage of leads generated that are Tier 1.",
    },
    {
      label: "Total Tier 2",
      value: teamLeadGenerationStats.totalTier2Leads,
      detail: "Generated leads",
      secondaryValue: tier2Percent,
      tooltip: "The value shown in parentheses is the percentage of leads generated that are Tier 2.",
    },
  ]

  return (
    <StatsSurface>
      <StatsHeader>
        <div>
          <SectionEyebrow>Team Totals</SectionEyebrow>
          <SectionTitle>Complete Lead Generation Statistics</SectionTitle>
        </div>
        <SectionSummary>Portfolio-wide lead generation totals from completed sweep activity.</SectionSummary>
      </StatsHeader>
      <StatsGrid>
        {stats.map((stat) => (
          <StatCard key={stat.label}>
            <StatLabelRow>
              <StatLabel>{stat.label}</StatLabel>
              {stat.tooltip &&
                <Tooltip
                  componentsProps={{tooltip: {sx: tooltipSx}}}
                  PopperProps={{modifiers: [{
                    name: "offset",
                    options: {
                      offset: [0, -6],
                    },
                  }]}}
                  title={stat.tooltip}
                  placement="top"
                  arrow
                >
                  <HelpButton aria-label={`${stat.label} context`} type="button">
                    <HelpOutlineIcon />
                  </HelpButton>
                </Tooltip>
              }
            </StatLabelRow>
            <StatValue>
              {stat.value}
              {stat.suffix && <Accent>{stat.suffix}</Accent>}
              {stat.secondaryValue !== undefined &&
                <SecondaryValue>
                  ({stat.secondaryValue}<Accent>%</Accent>)
                </SecondaryValue>
              }
            </StatValue>
            <StatDetail>{stat.detail}</StatDetail>
          </StatCard>
        ))}
      </StatsGrid>
    </StatsSurface>
  )
}

const StatsSurface = styled.section(({ theme }) => `
  min-width: 0;
  display: grid;
  gap: 0;
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.8rem;
  background: ${theme.palette.common.white};
  box-shadow: 0 0.25rem 0.9rem rgba(15, 23, 42, 0.05);
`)

const StatsHeader = styled.header(({ theme }) => `
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: end;
  padding: 0.65rem 0.8rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.grayBorderLight};
`)

const SectionEyebrow = styled.div(({ theme }) => `
  margin-bottom: 0.12rem;
  color: ${theme.palette.primary.main};
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.04rem;
  line-height: 1.2;
  text-transform: uppercase;
`)

const SectionTitle = styled.h2(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.black};
  font-size: 0.94rem;
  font-weight: 850;
  line-height: 1.2;
`)

const SectionSummary = styled.p(({ theme }) => `
  max-width: 25rem;
  margin: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.74rem;
  font-weight: 650;
  line-height: 1.25;
  text-align: right;
`)

const StatsGrid = styled.div(`
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0.55rem;
  padding: 0.65rem 0.75rem 0.75rem;
`)

const StatCard = styled.article(({ theme }) => `
  min-width: 0;
  min-height: 5.55rem;
  display: grid;
  align-content: space-between;
  gap: 0.4rem;
  padding: 0.58rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.62rem;
  background: ${theme.palette.common.cardBackground};
`)

const StatLabelRow = styled.div(`
  min-width: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.45rem;
`)

const StatLabel = styled.span(({ theme }) => `
  min-width: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.7rem;
  font-weight: 800;
  line-height: 1.25;
`)

const HelpButton = styled.button(({ theme }) => `
  width: 1.35rem;
  height: 1.35rem;
  min-width: 1.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 999rem;
  background: transparent;
  color: ${theme.palette.common.grayText};
  cursor: help;

  svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    color: ${theme.palette.primary.dark};
    background: ${theme.palette.primary.labelTransparent};
  }
`)

const StatValue = styled.div(({ theme }) => `
  min-width: 0;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.2rem;
  color: ${theme.palette.common.black};
  font-size: 1.12rem;
  font-weight: 850;
  line-height: 1.05;
`)

const Accent = styled.span(({ theme }) => `
  color: ${theme.palette.primary.pastelMedium};
  font-size: 0.88rem;
  font-weight: 850;
`)

const SecondaryValue = styled.span(({ theme }) => `
  margin-left: 0.2rem;
  color: ${theme.palette.common.grayText};
  font-size: 0.78rem;
  font-weight: 750;
`)

const StatDetail = styled.div(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1.2;
`)

export default TeamStats;
